// report.js
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";

export function generateReport({
	data,
	columns,
	format = "pdf",
	fileName,
	filters,
	filtersOrder, // ex.: ['param','paramDate','active','dtIni','dtFim']
	headerSubtitle, // ex.: "Cadastro"
	totals, // objeto { key: { label, value, format } } — vindo do listDataTotal do matrix
}) {
	// --- utils ---
	function normalize(v) {
		if (v == null) return "";
		if (Array.isArray(v)) return v.map(normalize).join(", ");
		if (typeof v === "object") return JSON.stringify(v);
		if (typeof v === "boolean") return v ? "SIM" : "NÃO";
		return String(v);
	}

	function formatValue(v, column) {
		if (v == null) return "";
		if (column.format === "currency") {
			return Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
		}
		return normalize(v);
	}

	const filtersRows = (() => {
		if (!filters) return [];
		if (filtersOrder && filtersOrder.length) {
			return filtersOrder
				.filter((k) => !!filters[k])
				.map((k) => {
					const f = filters[k];
					return [f.label || k, normalize(f.value)];
				});
		}
		return Object.keys(filters).map((k) => {
			const f = filters[k];
			return [f.label || k, normalize(f.value)];
		});
	})();

	const safeFileName = `${fileName || "relatorio"}`;

	// ===================== PDF =====================
	if (format === "pdf") {
		const doc = new jsPDF("p", "pt");
		const margin = 40;
		const pageW = doc.internal.pageSize.getWidth();

		// Cabeçalho — Título (esq)
		const topY = 36;
		doc.setFontSize(18);
		doc.setFont(undefined, "normal");
		doc.text(fileName || "Relatório", margin, topY);

		// Linha azul sob o título (opcional)
		doc.setDrawColor(41, 128, 185);
		doc.setLineWidth(2);
		doc.line(margin, topY + 6, margin + 40, topY + 6);

		if (headerSubtitle) {
			doc.setFontSize(9); // Reduzido de 10 para 9
			doc.setTextColor(120);
			doc.text(headerSubtitle, margin, topY + 14);
			doc.setTextColor(0);
		}

		const rightX = pageW - margin; // régua da direita
		let y = topY;

		if (filtersRows.length) {
			doc.setFontSize(9); // Título dos filtros um pouco menor

			// título
			doc.setFont(undefined, "bold");
			doc.text("Filtros Aplicados", rightX, y, { align: "right" });
			y += 10; // Espaçamento reduzido de 12 para 10

			const lineH = 9; // Tamanho da linha reduzido de 12 para 9
			const maxLineW = 260; // largura máxima antes de quebrar linha

			doc.setFontSize(8); // Fonte dos filtros reduzida para 8
			filtersRows.forEach(([label, value]) => {
				const textLine = `${label}: ${value || ""}`;

				// quebra se for muito grande
				const lines = doc.splitTextToSize(textLine, maxLineW);

				doc.setFont(undefined, "normal");
				lines.forEach((line, i) => {
					doc.text(line, rightX, y + lineH * i, { align: "right" });
				});

				y += lineH * lines.length;
			});
		}

		// Imprime os totais de rodapé logo abaixo dos filtros aplicados no cabeçalho
		const hasTotals = totals && Object.keys(totals).length > 0;
		if (hasTotals) {
			y += 6;
			doc.setFont(undefined, "bold");
			doc.setFontSize(9);
			Object.keys(totals).forEach((key) => {
				const t = totals[key];
				// Ignora chave duplicada se houver "soma" e "valor" juntas
				if (key === "soma") return;

				let formattedVal = normalize(t.value);
				if (t.format === "currency") {
					formattedVal = Number(t.value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
				}
				const totalLine = `${t.label || key}: ${formattedVal}`;
				doc.text(totalLine, rightX, y, { align: "right" });
				y += 11;
			});
		}

		// Tabela começa abaixo do cabeçalho
		const tableStartY = Math.max(topY + 24, y + 10);
		// desenha uma linha horizontal separadora
		doc.setDrawColor(180); // cinza claro (pode usar 0 pra preto ou [r,g,b])
		doc.setLineWidth(1); // espessura da linha
		doc.line(
			margin, // X inicial (mesmo alinhamento da tabela)
			tableStartY - 6, // Y (um pouco acima do startY da tabela)
			pageW - margin, // X final (até o outro lado da tabela)
			tableStartY - 6
		);
		// ——— alinhamento dinâmico por conteúdo ———
		const looksNumeric = (v) => {
			if (v === null || v === undefined || v === "") return null; // ignora vazio
			if (typeof v === "number") return true;
			if (typeof v !== "string") return false;

			const s = v.trim().replace(/\s+/g, "");
			// aceita R$, $, %, separadores .,,
			if (!/^[-+]?((R\$)|\$)?[\d.,]+%?$/.test(s)) return false;

			const num = Number(
				s
					.replace(/R\$|\$|%/gi, "")
					.replace(/\./g, "") // milhares
					.replace(",", ".") // decimal
			);
			return !Number.isNaN(num);
		};

		// decide alinhamento por coluna olhando uma amostra das linhas
		const decideNumericCols = (columns, data, sampleN = 50, threshold = 0.6) =>
			columns.map((c) => {
				// permite forçar via columns[i].align = 'right' | 'left' | 'center'
				if (c.align) return c.align === "right";
				let seen = 0,
					numeric = 0;
				const n = Math.min(sampleN, data.length);
				for (let i = 0; i < n; i++) {
					const val = data[i]?.[c.key];
					const res = looksNumeric(val);
					if (res !== null) {
						seen++;
						if (res) numeric++;
					}
				}
				return seen > 0 && numeric / seen >= threshold;
			});

		const numericCols = decideNumericCols(columns, data);

		// monta columnStyles dinamicamente
		const columnStyles = {};
		columns.forEach((c, idx) => {
			const forced = c.align; // se veio alinhamento explícito
			columnStyles[idx] = {
				halign: forced || (numericCols[idx] ? "right" : idx === 0 ? "left" : "left"),
			};
		});

		const footRow = [];

		// ——— chamada do autoTable com o “visual” do seu print ———
		autoTable(doc, {
			startY: tableStartY,
			head: [columns.map((c) => c.label)],
			body: data.map((row) => columns.map((c) => formatValue(row[c.key], c))),
			foot: footRow,

			theme: "striped", // Mudado para striped para um visual mais limpo
			styles: {
				fontSize: 8.5, // Reduzido para 8.5pt (padrão profissional)
				cellPadding: { top: 7, right: 8, bottom: 7, left: 8 },
				lineColor: [239, 242, 245], // Borda suave entre as linhas
				lineWidth: 0.1, // Linhas bem sutis
				valign: "middle",
				textColor: [50, 50, 50],
			},
			headStyles: {
				fillColor: [44, 62, 80], // Azul escuro profissional (Slate)
				textColor: [255, 255, 255], // Texto branco para alto nível de contraste
				fontStyle: "bold",
				halign: "left",
				lineWidth: 0,
			},
			alternateRowStyles: {
				fillColor: [251, 252, 253], // Zebra cinza quase imperceptível
			},

			bodyStyles: {
				fillColor: [255, 255, 255],
			},

			columnStyles, // <- alinhamento dinâmico entra aqui
			footStyles: {
				fillColor: [255, 255, 255],
				textColor: [50, 50, 50],
				fontStyle: "bold",
				lineWidth: 0,
			},
			showFoot: footRow.length > 0 ? "lastPage" : "never",

			didDrawPage: (data) => {
				const pageWidth = doc.internal.pageSize.getWidth();
				const pageHeight = doc.internal.pageSize.getHeight();
				const margin = 40;

				doc.setFontSize(8);
				doc.setTextColor(150);
				doc.setFont(undefined, "normal");

				// Data e Hora de geração (esquerda)
				const now = new Date().toLocaleString("pt-BR");
				doc.text(`Gerado em: ${now}`, margin, pageHeight - 20);

				// Paginação (direita)
				doc.text(`Página ${doc.internal.getNumberOfPages()}`, pageWidth - margin, pageHeight - 20, {
					align: "right",
				});
			},
		});

		const blob = doc.output("blob");
		const blobUrl = URL.createObjectURL(blob);
		window.open(blobUrl, "_blank");
		return;
	}

	// ===================== EXCEL =====================
	// Aba "Relatório"
	const rows = data.map((item) => {
		const out = {};
		columns.forEach((c) => (out[c.label] = item[c.key]));
		return out;
	});
	const wsData = XLSX.utils.json_to_sheet(rows);

	// Aba "Filtros"
	let wsFilters = null;
	if (filtersRows.length) {
		const filtersTable = [["Filtro", "Valor"], ...filtersRows];
		wsFilters = XLSX.utils.aoa_to_sheet(filtersTable);
	}

	const wb = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(wb, wsData, "Relatório");
	if (wsFilters) XLSX.utils.book_append_sheet(wb, wsFilters, "Filtros");

	// larguras simples
	wsData["!cols"] = columns.map(() => ({ wch: 20 }));
	if (wsFilters) wsFilters["!cols"] = [{ wch: 25 }, { wch: 40 }];

	const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
	saveAs(new Blob([buf]), `${safeFileName}.xlsx`);
}
